import { type AdmittedStudentInfo } from 'AppBranding';
import { type ProfessionalOrganization } from 'CareerProfiles';
import { type CohortIguanaObject } from 'Cohorts';
import { type ProgramInclusion, CurriculumStatus } from 'ProgramInclusion';
import { type CurrentUserIguanaObject } from 'Users';
import { SCHEDULE_ADMISSIONS_CHAT_LINK } from 'AdmissionsHelp/constants';
import { type ExecEdEligibilityBundle } from 'ExecEdEligibility';
import {
    ProgramTypeConfigs,
    showsCertificateCheckInWhenInFinishedState,
    showsDiplomaCheckInWhenInFinishedState,
} from 'Program';
import {
    AdmissionsInfoBox,
    CoachingBox,
    CompanyTuitionFundingBox,
    CompleteApplicationBox,
    FindCompanyStudentsBox,
    FindFriendsBox,
    FollowUsBox,
    NotJoiningProgramCannotReapplyBox,
    NotJoiningProgramCanReapplyBox,
    OfflineBox,
    OpenCoursesBox,
    OurDegreeProgramsBox,
    OurTuitionModelBox,
    PreApprovedForCohortBox,
    RegisteredBox,
    RegisterForProgramBox,
    PreRecordedInterviewBox,
    ScheduleYourInterviewBox,
    ApplicationReceivedBox,
    StatusBox,
    DiplomaCheckBox,
    DiplomaFramesBox,
    TranscriptsBox,
    RecentCoursesBox,
    DigitalCertificateCheckBox,
    EnrollmentBox,
    WelcomeBox,
    ScheduleBox,
    AdmittedStudentsZoomBox,
    admittedStudentsZoomStatus,
    EmployerFundingBox,
    IdentityVerificationBox,
    UploadDocumentsBox,
    LateProjectSubmissionBox,
} from 'StudentDashboardSidebar';
import { SidebarBoxKey, type SidebarBoxConfig } from './StudentDashboard.types';

type Props = {
    admissionsSupportEmail: string;
    featuredWorkExperienceCompany: ProfessionalOrganization | null;
    inOfflineMode: boolean;
    admittedStudentInfo: AdmittedStudentInfo;
    relevantCohort: CohortIguanaObject | null;
    curriculumStatus: CurriculumStatus | null;
    activeProgramInclusion: ProgramInclusion | null;
    userName: CurrentUserIguanaObject['name'];
    mailingAddress: CurrentUserIguanaObject['mailingAddress'];
    preApprovalBundleActivated: boolean;
    filteredEligibilityBundle: ExecEdEligibilityBundle | null;
    showEnrollment: boolean;
    showIdentityVerification: boolean;
};

// This function is called from studentDashboardConfig, which is memoized. For that reason, this function is
// intended to return the same value whenever it is called with the same arguments. So, all arguments here should
// be immutable.
// oxlint-disable-next-line max-lines-per-function, complexity
const sidebarBoxConfigs = ({
    admissionsSupportEmail,
    featuredWorkExperienceCompany,
    inOfflineMode,
    admittedStudentInfo,
    relevantCohort,
    curriculumStatus,
    activeProgramInclusion,
    userName,
    mailingAddress,
    preApprovalBundleActivated,
    filteredEligibilityBundle,
    showEnrollment,
    showIdentityVerification,
}: Props): Record<SidebarBoxKey, SidebarBoxConfig> => {
    const { friendly_name: friendlyName, text } = featuredWorkExperienceCompany || {};
    const featuredCompanyName = friendlyName || text || '';
    const pastDueProjects = activeProgramInclusion?.pastDueProjects ?? [];

    return {
        [SidebarBoxKey.admissions_info]: {
            key: SidebarBoxKey.admissions_info,
            desktopContent: (
                <AdmissionsInfoBox
                    admissionsEmail={admissionsSupportEmail}
                    scheduleVideoChatUrl={SCHEDULE_ADMISSIONS_CHAT_LINK}
                />
            ),
            mobileContent: (
                <AdmissionsInfoBox
                    admissionsEmail={admissionsSupportEmail}
                    scheduleVideoChatUrl={SCHEDULE_ADMISSIONS_CHAT_LINK}
                    mobile
                />
            ),
            shouldHide: inOfflineMode,
        },
        [SidebarBoxKey.admissions_timeline]: {
            key: SidebarBoxKey.admissions_timeline,
            shouldHide: true,
        },
        [SidebarBoxKey.admitted_students_zoom]: {
            key: SidebarBoxKey.admitted_students_zoom,
            desktopContent: <AdmittedStudentsZoomBox />,
            mobileContent: <AdmittedStudentsZoomBox mobile />,
            shouldHide: admittedStudentsZoomStatus(admittedStudentInfo) === 'hidden',
        },
        [SidebarBoxKey.application_received]: {
            key: SidebarBoxKey.application_received,
            desktopContent: <ApplicationReceivedBox />,
        },
        [SidebarBoxKey.company_tuition_funding]: {
            key: SidebarBoxKey.company_tuition_funding,
            desktopContent: <CompanyTuitionFundingBox companyName={featuredCompanyName} />,
        },
        [SidebarBoxKey.complete_application]: {
            key: SidebarBoxKey.complete_application,
            desktopContent: <CompleteApplicationBox />,
            mobileContent: <CompleteApplicationBox mobile />,
            shouldHide: inOfflineMode,
        },
        [SidebarBoxKey.employer_funding]: {
            key: SidebarBoxKey.employer_funding,
            desktopContent: <EmployerFundingBox />,
        },
        [SidebarBoxKey.find_company_students]: {
            key: SidebarBoxKey.find_company_students,
            desktopContent: <FindCompanyStudentsBox companyName={featuredCompanyName} />,
            mobileContent: <FindCompanyStudentsBox companyName={featuredCompanyName} mobile />,
            shouldHide: inOfflineMode,
        },
        [SidebarBoxKey.find_friends]: {
            key: SidebarBoxKey.find_friends,
            desktopContent: <FindFriendsBox />,
            mobileContent: <FindFriendsBox mobile />,
            shouldHide: inOfflineMode,
        },
        [SidebarBoxKey.follow_us]: {
            key: SidebarBoxKey.follow_us,
            desktopContent: <FollowUsBox />,
        },
        [SidebarBoxKey.identity_verification]: {
            key: SidebarBoxKey.identity_verification,
            desktopContent: <IdentityVerificationBox />,
            mobileContent: <IdentityVerificationBox mobile />,
            shouldHide: !showIdentityVerification,
        },
        [SidebarBoxKey.open_courses]: {
            key: SidebarBoxKey.open_courses,
            desktopContent: <OpenCoursesBox />,
            mobileContent: <OpenCoursesBox mobile />,
        },
        [SidebarBoxKey.our_degree_programs]: {
            key: SidebarBoxKey.our_degree_programs,
            desktopContent: <OurDegreeProgramsBox />,
        },
        [SidebarBoxKey.our_tuition_model]: {
            key: SidebarBoxKey.our_tuition_model,
            desktopContent: <OurTuitionModelBox />,
        },
        [SidebarBoxKey.register_for_program]: {
            key: SidebarBoxKey.register_for_program,
            desktopContent: <RegisterForProgramBox />,
            mobileContent: <RegisterForProgramBox mobile />,
        },
        [SidebarBoxKey.registered]: {
            key: SidebarBoxKey.registered,
            desktopContent: <RegisteredBox />,
            mobileContent: <RegisteredBox mobile />,
        },
        [SidebarBoxKey.not_joining_program_can_reapply]: {
            key: SidebarBoxKey.not_joining_program_can_reapply,
            desktopContent: <NotJoiningProgramCanReapplyBox />,
            mobileContent: <NotJoiningProgramCanReapplyBox mobile />,
        },
        [SidebarBoxKey.not_joining_program_cannot_reapply]: {
            key: SidebarBoxKey.not_joining_program_cannot_reapply,
            desktopContent: <NotJoiningProgramCannotReapplyBox />,
            mobileContent: <NotJoiningProgramCannotReapplyBox mobile />,
        },
        [SidebarBoxKey.schedule_your_interview]: {
            key: SidebarBoxKey.schedule_your_interview,
            desktopContent: <ScheduleYourInterviewBox />,
            mobileContent: <ScheduleYourInterviewBox mobile />,
        },
        [SidebarBoxKey.pre_recorded_interview]: {
            key: SidebarBoxKey.pre_recorded_interview,
            desktopContent: <PreRecordedInterviewBox />,
            mobileContent: <PreRecordedInterviewBox mobile />,
        },
        [SidebarBoxKey.upload_documents]: {
            key: SidebarBoxKey.upload_documents,
            desktopContent: <UploadDocumentsBox />,
        },
        [SidebarBoxKey.view_upcoming_events]: {
            key: SidebarBoxKey.view_upcoming_events,
            // v1-only box, always hidden in v2
            shouldHide: true,
        },
        [SidebarBoxKey.upcoming_events]: {
            key: SidebarBoxKey.upcoming_events,
            // v1-only box, always hidden in v2
            shouldHide: true,
        },
        [SidebarBoxKey.this_week_in_slack]: {
            key: SidebarBoxKey.this_week_in_slack,
            // v1-only box, always hidden in v2
            shouldHide: true,
        },
        [SidebarBoxKey.schedule]: {
            key: SidebarBoxKey.schedule,
            desktopContent: <ScheduleBox />,
            shouldHide:
                !relevantCohort ||
                !relevantCohort.currentPeriod ||
                !ProgramTypeConfigs[relevantCohort.programType].supportsScheduleSidebarBox(relevantCohort) ||
                !curriculumStatus ||
                ![
                    CurriculumStatus.Week1,
                    CurriculumStatus.OnTrack,
                    CurriculumStatus.OnTrackFinished,
                    CurriculumStatus.NotOnTrack,
                    CurriculumStatus.AlmostThere,
                    CurriculumStatus.PreGraduationNotOnTrack,
                    CurriculumStatus.PreGraduationAlmostThere,
                ].includes(curriculumStatus),
        },
        [SidebarBoxKey.schedule_status]: {
            key: SidebarBoxKey.schedule_status,
            desktopContent: <StatusBox />,
            mobileContent: <StatusBox mobile />,
            shouldHide:
                !relevantCohort ||
                !ProgramTypeConfigs[relevantCohort.programType].supportsScheduleStatusSidebarBox(relevantCohort) ||
                (curriculumStatus === CurriculumStatus.NotOnTrack && !activeProgramInclusion) ||
                curriculumStatus === CurriculumStatus.WithdrawnOrExpelled,
        },
        [SidebarBoxKey.recent_courses]: {
            key: SidebarBoxKey.recent_courses,
            desktopContent: <RecentCoursesBox />,
        },
        [SidebarBoxKey.diploma_check]: {
            key: SidebarBoxKey.diploma_check,
            desktopContent: <DiplomaCheckBox userName={userName} mailingAddress={mailingAddress} />,
            shouldHide: !relevantCohort || !showsDiplomaCheckInWhenInFinishedState(relevantCohort.programType),
        },
        [SidebarBoxKey.digital_certificate_check]: {
            key: SidebarBoxKey.digital_certificate_check,
            desktopContent: <DigitalCertificateCheckBox userName={userName} />,
            shouldHide: !relevantCohort || !showsCertificateCheckInWhenInFinishedState(relevantCohort.programType),
        },
        [SidebarBoxKey.diploma_frames]: {
            key: SidebarBoxKey.diploma_frames,
            desktopContent: <DiplomaFramesBox />,
            shouldHide: !relevantCohort || !relevantCohort.supportsSchedule || !relevantCohort.supportsPhysicalDiploma,
        },
        [SidebarBoxKey.enrollment]: {
            key: SidebarBoxKey.enrollment,
            desktopContent: <EnrollmentBox />,
            mobileContent: <EnrollmentBox mobile />,
            shouldHide: !showEnrollment,
        },
        [SidebarBoxKey.offline]: {
            key: SidebarBoxKey.offline,
            desktopContent: <OfflineBox />,
            mobileContent: <OfflineBox mobile />,
            shouldHide: !inOfflineMode,
        },
        [SidebarBoxKey.transcripts]: {
            key: SidebarBoxKey.transcripts,
            desktopContent: <TranscriptsBox />,
            // FIXME: ExecEd programs won't display transcript information at launch, but may in the future
            shouldHide: !relevantCohort || !relevantCohort.supportsSchedule || relevantCohort.isExecEd,
        },
        [SidebarBoxKey.graduated]: {
            key: SidebarBoxKey.graduated,
            // v1-only box, always hidden in v2
            shouldHide: true,
        },
        [SidebarBoxKey.accepted]: {
            key: SidebarBoxKey.accepted,
            // v1-only box, always hidden in v2
            shouldHide: true,
        },
        [SidebarBoxKey.pre_approved_for_cohort]: {
            key: SidebarBoxKey.pre_approved_for_cohort,
            desktopContent: <PreApprovedForCohortBox filteredEligibilityBundle={filteredEligibilityBundle!} />,
            mobileContent: <PreApprovedForCohortBox filteredEligibilityBundle={filteredEligibilityBundle!} mobile />,
            shouldHide:
                inOfflineMode ||
                !preApprovalBundleActivated ||
                (!filteredEligibilityBundle?.recommendedProgramType &&
                    !filteredEligibilityBundle?.eligiblePrograms?.length),
        },
        [SidebarBoxKey.late_project_submission]: {
            key: SidebarBoxKey.late_project_submission,
            desktopContent: <LateProjectSubmissionBox />,
            shouldHide: pastDueProjects.length === 0 || curriculumStatus === CurriculumStatus.NotOnTrack,
        },
        [SidebarBoxKey.coaching]: {
            key: SidebarBoxKey.coaching,
            desktopContent: <CoachingBox />,
            mobileContent: <CoachingBox mobile />,
            shouldHide: inOfflineMode,
        },
        [SidebarBoxKey.welcome]: {
            key: SidebarBoxKey.welcome,
            desktopContent: <WelcomeBox />,
        },
    };
};

export default sidebarBoxConfigs;
